import Router from 'next/router';
import { lastValueFrom } from 'rxjs';

import {
  changeBoxCustomization, changeBoxCustomizationSuccess,
  changeCustomizationStep, changeCustomizationStepSuccess,
  customizationNextStep, customizationPrevStep, updateCustomizationAttribute
} from '../slices/customization.slice';

import { showBoxCustomizationModal } from '../slices/gui.slice';
import { addApiErrorToast } from '../slices/toast.slice';
import { getCart } from '../slices/cart.slice';

import { isObjectEqual } from '@/utils/comparisonUtils';
import {
  customizationSetNextStep,
  customizationSetPrevStep,
  customizationSwitchMode,
  formatToSave,
  getAttributeToSave
} from '@/utils/customizationUtils';
import { AppListenerEntry } from '@/redux';
import { STORE_ID_DEFAULT } from '@/constants/stores';
import { CUSTOMIZATION_NAMES, CUSTOMIZATION_MODES } from '@/constants/customization';
import { PAGES } from '@/constants/pages';

export const epicChangeBoxCustomization: AppListenerEntry = {
  actionCreator: changeBoxCustomization,
  effect: async ({ payload }: ReturnType<typeof changeBoxCustomization>, { getState, dispatch, extra: { boxService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { boxId, customization, customizationQt, redirection, isGetCart } = payload;
    try {
      const content = await lastValueFrom(boxService.changeCustomization(storeId, boxId, customization, customizationQt));
      dispatch(changeBoxCustomizationSuccess({ content }));
      if (redirection) {
        dispatch(changeCustomizationStep(redirection));
      }
      if (isGetCart) {
        dispatch(getCart());
      }
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicUpdateCustomizationAttribute: AppListenerEntry = {
  actionCreator: updateCustomizationAttribute,
  effect: async ({ payload }: ReturnType<typeof updateCustomizationAttribute>, { getState, dispatch, extra: { boxService } }) => {
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;
    const { id, attributeName, newValue, redirection } = payload;
    try {
      const content = await lastValueFrom(boxService.updateCustomizationAttribute(storeId, id, attributeName, newValue));
      dispatch(changeBoxCustomizationSuccess({ content }));
      if (redirection) {
        dispatch(changeCustomizationStep(redirection));
      }
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicCustomizationNextStep: AppListenerEntry = {
  actionCreator: customizationNextStep,
  effect: ({ payload }: ReturnType<typeof customizationNextStep>, { getState, dispatch }) => {
    const { id, step, options, customization, initialCustomization, customizationQt } = payload;
    const { isMobileDevice } = getState().gui;
    const { mode, returnUrl } = options;

    if (isMobileDevice) {
      if (mode === CUSTOMIZATION_MODES.PREVIEW && [CUSTOMIZATION_NAMES.TEXT, CUSTOMIZATION_NAMES.SIGNATURE].includes(step.url)) {
        customizationSwitchMode(id, step.url, options, CUSTOMIZATION_MODES.EDIT);
        return;
      }
      if (mode === CUSTOMIZATION_MODES.EDIT && step.url === CUSTOMIZATION_NAMES.SIGNATURE) {
        customizationSwitchMode(id, step.url, options, CUSTOMIZATION_MODES.POSITION);
        return;
      }
    }

    if (step.url === CUSTOMIZATION_NAMES.REVIEW && returnUrl) {
      Router.push(PAGES[returnUrl.toUpperCase()]);
      return;
    }

    if (step.url === CUSTOMIZATION_NAMES.REPORT) {
      dispatch(showBoxCustomizationModal({ currentBox: undefined }));
    } else if (isObjectEqual(customization, initialCustomization)) {
      dispatch(changeCustomizationStep({ id, step, options, isNextStep: true }));
    } else {
      const formatedCustomization = formatToSave(customization);
      const redirection = { id, step, options };
      if (customizationQt) {
        // save by attribute
        const formatedInitialCustomization = formatToSave(initialCustomization);
        const { attributeName, newValue } = getAttributeToSave(formatedCustomization, formatedInitialCustomization);
        dispatch(updateCustomizationAttribute({ id, attributeName, newValue, redirection }));
      } else {
        // save all customization
        dispatch(changeBoxCustomization({
          boxId: id, customization: formatedCustomization, customizationQt: 1, redirection, isGetCart: false
        }));
      }
    }
  }
};

export const epicCustomizationPrevStep: AppListenerEntry = {
  actionCreator: customizationPrevStep,
  effect: ({ payload }: ReturnType<typeof customizationPrevStep>, { getState, dispatch }) => {
    const { isMobileDevice } = getState().gui;
    const { id, step, options } = payload;
    const { mode } = options;

    if (isMobileDevice) {
      if (mode === CUSTOMIZATION_MODES.POSITION) {
        customizationSwitchMode(id, step.url, options, CUSTOMIZATION_MODES.EDIT);
        return;
      }
      if (mode === CUSTOMIZATION_MODES.EDIT) {
        customizationSwitchMode(id, step.url, options, CUSTOMIZATION_MODES.PREVIEW);
        return;
      }
    }

    if (mode === CUSTOMIZATION_MODES.PREVIEW && step.url === CUSTOMIZATION_NAMES.TEXT) {
      Router.push(PAGES.HOME);
      return;
    }

    return dispatch(changeCustomizationStep({ id, step, options, isNextStep: false }));
  }
};

export const epicChangeCustomizationStep: AppListenerEntry = {
  actionCreator: changeCustomizationStep,
  effect: ({ payload }: ReturnType<typeof changeCustomizationStep>, { dispatch }) => {
    const { id, step, options, isNextStep = true } = payload;
    if (isNextStep) {
      customizationSetNextStep(id, step, options);
    } else {
      customizationSetPrevStep(id, step, options);
    }
    return dispatch(changeCustomizationStepSuccess());
  }
};
